import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  CoveredMatrixGroup,
  CoveredMatrixCallOption,
  CoveredMatrixMarketWatch,
  CoveredMatrixPortfolioImbalance,
  CoveredMatrixLiveCriterion,
  CoveredMatrixLiveCriterionRequest,
  CoveredMatrixOrderItemTableReport,
  CoveredMatrixOrderItemTableSearchParams,
  CoveredMatrixExecutionTableSearchParams,
  CoveredMatrixTradeTableReport,
  CoveredMatrixErrorTableReport,
  CoveredMatrixOrderTableReport,
  CoveredMatrixOrderTableSearchParams,
  CoveredMatrixOrderPreviewResult,
  CreateCoveredMatrixOrderRequest,
  UpdateCoveredMatrixCriteriaRequest,
} from "src/Interfaces/coveredMatrixOrder";
import { getRequest, postRequest, putRequest, deleteRequest, getTable } from "./requestTemplates";

const controller = "/v1/CoveredMatrixOrder";

export const getCoveredMatrixOrders = (accountId?: number): Promise<CoveredMatrixGroup[]> =>
  getRequest(`${controller}/List${accountId ? `?accountId=${accountId}` : ""}`);

export const getCoveredMatrixOrdersTable = (
  searchParams: CoveredMatrixOrderTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
) => getTable<CoveredMatrixOrderTableSearchParams, CoveredMatrixOrderTableReport>(
  `${controller}/Table`, searchParams, offset, pageSize, sortingType,
);

export const getCoveredMatrixOrderItemsTable = (
  searchParams: CoveredMatrixOrderItemTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
) => getTable<CoveredMatrixOrderItemTableSearchParams, CoveredMatrixOrderItemTableReport>(
  `${controller}/ItemTable`, searchParams, offset, pageSize, sortingType,
);

export const getCoveredMatrixTradesTable = (
  searchParams: CoveredMatrixExecutionTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
) => getTable<CoveredMatrixExecutionTableSearchParams, CoveredMatrixTradeTableReport>(
  `${controller}/TradeTable`, searchParams, offset, pageSize, sortingType,
);

export const getCoveredMatrixErrorsTable = (
  searchParams: CoveredMatrixExecutionTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
) => getTable<CoveredMatrixExecutionTableSearchParams, CoveredMatrixErrorTableReport>(
  `${controller}/ErrorTable`, searchParams, offset, pageSize, sortingType,
);

export const getCoveredMatrixOrder = (id: number): Promise<CoveredMatrixGroup> =>
  getRequest(`${controller}/Get?id=${id}`);

export const getCoveredMatrixPortfolioImbalance = (groupId: number): Promise<CoveredMatrixPortfolioImbalance> =>
  getRequest(`${controller}/GetPortfolioCoveredCallImbalance?groupId=${groupId}`);

export const createCoveredMatrixOrder = (
  request: CreateCoveredMatrixOrderRequest,
): Promise<{ batchId: string; groups: CoveredMatrixGroup[]; errors: string[]; warnings: string[] }> =>
  postRequest(`${controller}/Create`, request);

export const previewCoveredMatrixOrder = (
  request: CreateCoveredMatrixOrderRequest,
): Promise<CoveredMatrixOrderPreviewResult> =>
  postRequest(`${controller}/Preview`, request);

export const updateCoveredMatrixCriteria = (
  request: UpdateCoveredMatrixCriteriaRequest,
): Promise<void> => putRequest(`${controller}/UpdateCriteria`, request);

export const deleteCoveredMatrixOrder = (id: number): Promise<void> =>
  deleteRequest(`${controller}/Delete?id=${id}`);

export const setCoveredMatrixOrderActive = (id: number, isActive: boolean): Promise<void> =>
  putRequest(`${controller}/SetActive?id=${id}&isActive=${isActive}`, null);

export const getCoveredMatrixAccounts = (): Promise<DropdownItem[]> =>
  getRequest("/v1/sahra_user/list?exchangetype=2");

export const getCoveredMatrixBaseInstruments = (): Promise<DropdownItem[]> =>
  getRequest("/v1/coveredcall/GetBaseInstruments");

export const getCoveredMatrixCallOptions = (baseIsin: string): Promise<CoveredMatrixCallOption[]> =>
  getRequest(`${controller}/GetCallOptions?baseIsin=${encodeURIComponent(baseIsin)}`);

export const getCoveredMatrixTseLinks = (instrumentIds: string[]): Promise<Record<string, string>> =>
  postRequest(`${controller}/GetTseLinks`, instrumentIds);

export const getCoveredMatrixMarketWatch = (instrumentIds: string[]): Promise<Record<string, CoveredMatrixMarketWatch>> =>
  postRequest(`${controller}/GetMarketWatch`, instrumentIds);

export const getCoveredMatrixLiveCriteria = (
  request: CoveredMatrixLiveCriterionRequest,
): Promise<CoveredMatrixLiveCriterion[]> =>
  postRequest(`${controller}/GetLiveCriteria`, request);
